import tagMovieApi from 'src/apis/tagMovie';
import { reactive, type Reactive } from 'vue';
import { createInjection } from 'src/lib/createInjection';
import { logTagMovieAdd, logTagMovieDelete } from 'src/lib/analyticsHelper';
import { getAggregatedMovieTagsFromMovieList, type ResultTag } from 'src/lib/tagModalHelper';
import type { EditMovieTagModalState } from 'src/models/top';
import type { MapElemInfo } from 'src/models';
import type { GIMovieList, GIResource } from 'src/models/geoItem';
import type { Tag } from 'src/models/tag';

// provideMovieTagEditが構築・provideするstoreの公開インターフェース
export interface MovieTagEditStore {
  editMovieTagModal: Reactive<EditMovieTagModalState>;
  startEditMovieTag: (obj: MapElemInfo<GIMovieList>) => Promise<void>;
  updateMovieTagsOfMovieList: (resultTags: ResultTag[]) => Promise<void>;
}

const { provide: provideMovieTagEditCtx, inject: injectMovieTagEditCtx } =
  createInjection<MovieTagEditStore>('useMovieTagEdit');

export function useMovieTagEditStore(): MovieTagEditStore {
  return injectMovieTagEditCtx();
}

// provide/injectは親→子方向のみで、provider自身はこのstoreをinjectできないため、
// provider側でも利用できるようprovideするだけでなくstoreを返り値でも渡す
export function provideMovieTagEdit(callbacks: {
  setMapSelectedElemInfo: (val: MapElemInfo<GIResource> | null) => void;
}): MovieTagEditStore {
  const editMovieTagModal = reactive<EditMovieTagModalState>({
    show: false,
    tags: [],
    movieList: null,
  });

  const startEditMovieTag = async(obj: MapElemInfo<GIMovieList>): Promise<void> => {
    editMovieTagModal.movieList = obj.data;
    editMovieTagModal.tags = getAggregatedMovieTagsFromMovieList(obj.data);
    editMovieTagModal.show = true;
  };

  const updateMovieTagsOfMovieList = async(resultTags: ResultTag[]): Promise<void> => {
    const movieList = editMovieTagModal.movieList;
    if (!movieList) return;

    let numMoviesTagAdded = 0;
    let numMoviesTagDeleted = 0;

    const promises = movieList.movies.map((m, idx) => {
      let adds = 0;
      let deletes = m.tags.length;
      const currentTagMap = m.tags.reduce<Record<number, Tag>>((acc, e) => {
        acc[e.id] = e; return acc;
      }, {});
      const tagsOfMovie = [];
      for (const resTag of resultTags) {
        // 完全選択且つ今自分についてなければ新たに追加したことになる
        if (!resTag.isPartiallySelected && !currentTagMap[resTag.id]) {
          ++adds;
        }
        // 完全/半 選択にかかわらず、元から自分についてるやつは削除件数から引き算
        if (currentTagMap[resTag.id]) {
          --deletes;
        }
        // 完全選択のもの、または半選択で且つ元から自分についているものを抽出
        if (!resTag.isPartiallySelected || currentTagMap[resTag.id]) {
          tagsOfMovie.push(resTag);
        }
      }
      if (adds > 0) { ++numMoviesTagAdded; }
      if (deletes > 0) { ++numMoviesTagDeleted; }
      return (async() => {
        const updateResult = await tagMovieApi.update(m.id, { tag_ids: tagsOfMovie.map(e => e.id) });
        movieList.movies[idx].tags = updateResult.tags;
      })();
    });
    await Promise.all(promises);

    if (numMoviesTagAdded > 0) { logTagMovieAdd(numMoviesTagAdded); }
    if (numMoviesTagDeleted > 0) { logTagMovieDelete(numMoviesTagDeleted); }

    callbacks.setMapSelectedElemInfo({
      dataName: 'movie',
      data: movieList,
    });

    editMovieTagModal.show = false;
  };

  const store = {
    editMovieTagModal,
    startEditMovieTag,
    updateMovieTagsOfMovieList,
  };
  provideMovieTagEditCtx(store);
  return store;
}
